export enum TShirtSize {
  XS = 'xs',
  S = 's',
  M = 'm',
  L = 'l',
  XL = 'xl',
  XXL = 'xxl'
}

export enum JobStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled'
}

export enum FileType {
  PDF = 'pdf',
  DOCX = 'docx',
  TXT = 'txt',
  AUDIO = 'audio',
  MP3 = 'mp3',
  WAV = 'wav',
  M4A = 'm4a'
}

export enum StoryStatus {
  PENDING = 'pending',
  ACCEPTED = 'accepted',
  REJECTED = 'rejected',
  MODIFIED = 'modified'
}

export interface ComplexityMatrix {
  frontend: {
    pageComplexity: Record<TShirtSize, number>;
    uiComponents: Record<TShirtSize, number>;
    stateManagement: Record<TShirtSize, number>;
    apiIntegrations: Record<TShirtSize, number>;
  };
  backend: {
    dataModels: Record<TShirtSize, number>;
    businessLogic: Record<TShirtSize, number>;
    integrations: Record<TShirtSize, number>;
    security: Record<TShirtSize, number>;
  };
  middleware: {
    messageQueues: Record<TShirtSize, number>;
    caching: Record<TShirtSize, number>;
    dataTransformation: Record<TShirtSize, number>;
  };
  database: {
    schemaComplexity: Record<TShirtSize, number>;
    relationships: Record<TShirtSize, number>;
    performance: Record<TShirtSize, number>;
  };
}

export enum EstimationMethodology {
  T_SHIRT_SIZING = 't_shirt_sizing',
  FUNCTION_POINTS = 'function_points',
  COCOMO = 'cocomo'
}

export interface EffortMultipliers {
  projectSetup: number;
  management: number;
  testing: number;
  devOps: number;
  documentation: number;
  design: number;
}

export interface StoryGenerationJob {
  id: string;
  name: string;
  description?: string;
  fileType: FileType;
  fileName: string;
  filePath: string;
  status: JobStatus;
  extractedText?: string;
  generatedStories?: any[];
  errorMessage?: string;
  totalStories: number;
  acceptedStories: number;
  rejectedStories: number;
  createdById: string;
  projectId?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  project?: {
    id: string;
    name: string;
  };
}

export interface GeneratedStory {
  id: string;
  title: string;
  description?: string;
  acceptanceCriteria?: string;
  estimatedSize?: TShirtSize;
  estimatedEffort?: number;
  complexityFactors?: {
    frontend: number;
    backend: number;
    middleware: number;
    database: number;
  };
  // Methodology-specific data (collected after methodology selection)
  functionPointData?: {
    externalInputs: { simple: number; average: number; complex: number };
    externalOutputs: { simple: number; average: number; complex: number };
    externalInquiries: { simple: number; average: number; complex: number };
    internalLogicalFiles: { simple: number; average: number; complex: number };
    externalInterfaceFiles: { simple: number; average: number; complex: number };
  } | null;
  estimatedLinesOfCode?: number | null; // For COCOMO (in KLOC)
  status: StoryStatus;
  rejectionReason?: string;
  modifications?: string;
  jobId: string;
  reviewedById?: string;
  reviewedAt?: string;
  createdAt: string;
  updatedAt: string;
  reviewedBy?: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

export interface FunctionPointAnalysis {
  externalInputs: { simple: number; average: number; complex: number };
  externalOutputs: { simple: number; average: number; complex: number };
  externalInquiries: { simple: number; average: number; complex: number };
  internalLogicalFiles: { simple: number; average: number; complex: number };
  externalInterfaceFiles: { simple: number; average: number; complex: number };
  technicalComplexityFactors: {
    dataCommunications: number;
    distributedDataProcessing: number;
    performance: number;
    heavilyUsedConfiguration: number;
    transactionRate: number;
    onlineDataEntry: number;
    endUserEfficiency: number;
    onlineUpdate: number;
    codeReusability: number;
    installationEase: number;
    operationalEase: number;
    multipleSites: number;
    facilitateChange: number;
  };
  environmentalComplexityFactors: {
    familiarWithDevelopmentProcess: number;
    applicationExperience: number;
    objectOrientedProgrammingExperience: number;
    leadAnalystCapability: number;
    motivation: number;
    requirementsForStableRequirements: number;
    partTimeStaff: number;
    difficultProgrammingLanguage: number;
  };
}

export interface COCOMOModel {
  projectType: 'organic' | 'semi-detached' | 'embedded';
  linesOfCode: number;
  costDrivers: {
    requiredReliability: number;
    databaseSize: number;
    productComplexity: number;
    executionTimeConstraint: number;
    mainStorageConstraint: number;
    computerTurnaroundTime: number;
    analystCapability: number;
    applicationsExperience: number;
    programmerCapability: number;
    virtualMachineExperience: number;
    programmingLanguageExperience: number;
    modernProgrammingPractices: number;
    useOfSoftwareTools: number;
    requiredDevelopmentSchedule: number;
  };
}

export interface EstimationConfig {
  id: string;
  name: string;
  description?: string;
  methodology: EstimationMethodology;
  complexityMatrix: ComplexityMatrix;
  effortMultipliers: EffortMultipliers;
  tShirtSizing: Record<TShirtSize, number>;
  teamVelocity: number;
  functionPointAnalysis?: FunctionPointAnalysis;
  cocomoModel?: COCOMOModel;
  isActive: boolean;
  isDefault: boolean;
  createdById: string;
  projectId?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  project?: {
    id: string;
    name: string;
  };
}

export interface UploadDocumentRequest {
  name: string;
  description?: string;
  projectId?: string;
  file: File;
}

export interface ReviewStoryRequest {
  status: StoryStatus;
  rejectionReason?: string;
  modifications?: string;
}
